import {
  TODAY,
  YESTERDAY,
  SEVEN_DAYS,
  FOURTEEN_DAYS,
  WEEK_TO_DATE,
  MONTH_TO_DATE,
  THIRTY_DAYS,
  NINETY_DAYS,
  ALL_TIME,
  REPORTS_FILTERS,
  DATE_RANGES,
} from "../constants/reports";
import { reportsFilterMapping } from "src/constants/reports"
import moment, { Moment } from "moment";
import numeral from 'numeral';
import dateFormat from "dateformat";
import _ from "lodash";
import { CONDITIONS } from "src/constants/reports";
import { Operators } from "src/types/clusterFilters";

export interface AuthorObj {
  firstName?: string
  lastName?:  string
}
export type AppliedDate = { 
    type: string | null,
  startDate: Moment | null,
  endDate: Moment | null,
}

export type Author = AuthorObj;

export const getReportDatesForOption = (key) => {
  switch (key) {
    case TODAY:
      return {
        startDate: moment(),
        endDate: moment(),
      };

    case YESTERDAY:
      return {
        startDate: moment().subtract(1, "days"),
        endDate: moment().subtract(1, "days"),
      };

    case SEVEN_DAYS:
      return {
        startDate: moment().subtract(7, "days"),
        endDate: moment(),
      };

    case FOURTEEN_DAYS:
      return {
        startDate: moment().subtract(14, "days"),
        endDate: moment(),
      };

    case WEEK_TO_DATE:
      return {
        startDate: moment().startOf("week"),
        endDate: moment(),
      };

    case MONTH_TO_DATE:
      return {
        startDate: moment().startOf("month"),
        endDate: moment(),
      };

    case THIRTY_DAYS:
      return {
        startDate: moment().subtract(30, "days"),
        endDate: moment(),
      };

    case NINETY_DAYS:
      return {
        startDate: moment().subtract(90, "days"),
        endDate: moment(),
      };

    case ALL_TIME:
      return {
        startDate: moment().subtract(2, 'years'),
        endDate: moment(),
      };

    default:
      return {
        startDate: moment(),
        endDate: moment(),
      };
  }
};

export const daysDifference = (fromDate, toDate) => {
  return toDate.diff(fromDate, "days");
};

export const generateQueryParams = (value, interval) => {
  const type = _.get(value, 'type');
  const startDate = _.get(value, 'startDate');
  const endDate = _.get(value, 'endDate');

  return {
    dateRange: type,
    startDate: startDate.format('DD/MM/YYYY'),
    endDate: endDate.format('DD/MM/YYYY'),
    interval: interval
  }
}

export const parseQueryParams = (value) => {
  const type = _.get(value, 'dateRange');
  const startDate = _.get(value, 'startDate');
  const endDate = _.get(value, 'endDate');

  if(!type){
    return {
      type: FOURTEEN_DAYS,
      ...getReportDatesForOption(FOURTEEN_DAYS)
    }
  }

  return {
    type: type,
    startDate: moment(startDate, 'DD/MM/YYYY'),
    endDate: moment(endDate, 'DD/MM/YYYY')
  }
}

export const getConditionsForProperty = (key) => {

  var filter = CONDITIONS.find(item => {
    return item.type === key.type;
  });

  if(!filter){
    return [];
  }

  return filter.options;
}


export const prepareFilters = (filters, supportedEntities) => {
  var updatedFilters = (filters || []).filter(filter => {
    if(!supportedEntities || supportedEntities.length == 0){
      return true;
    }

    var property = filter.property;
    return _.includes(supportedEntities, property.type);
  }).filter(filter => {
    var property = filter.property;
    var conditionOptions = getConditionsForProperty(property);

    const operator = conditionOptions.find(item => {
      return item.key == filter.operator;
    });

    if(operator?.unary){
      return true;
    }

    var value = filter.value;
    return (value != null && value.length > 0);
  }).map(filter => {
    var property = filter.property;
    var operator = filter.operator;
    var value = filter.value;

    if(filter.property.type === 'user' && filter.operator === "has_role") {
      value = (filter.value || []).map(item => {
        if(typeof item === 'string' || item instanceof String){
          return item;
        }

        return item.optionId;
      });
    } else if(filter.property.type === 'stream' || filter.property.type === 'collection' || 
      filter.property.type === 'contact' || filter.property.type === 'user' ||
      filter.property.type === 'user_group' || filter.property.type === 'asset' ||
      filter.property.type === 'company'
    ){
      value = (filter.value || []).map(item => {
        if(typeof item === 'string' || item instanceof String){
          return item;
        }

        return item.id;
      });
    }

    return {
      property: property,
      operator: operator,
      value: value  
    }
  });

  return updatedFilters;
}

export const convertDatesToTimezone = (value) => {
  const startDate = _.get(value, 'startDate');
  const endDate = _.get(value, 'endDate');

  const startDateValue = startDate.startOf('day');
  const endDateValue = endDate.endOf('day');
  
  return {
    startDate: startDateValue.format('YYYY-MM-DDTHH:mm:ss+00:00'),
    endDate: endDateValue.format('YYYY-MM-DDTHH:mm:ss+00:00')
  }
}

export const prepareRequest = (request, supportedEntities) => {
  const filterRequest = {
    ...convertDatesToTimezone(request.reportDate),
    filters: prepareFilters(request.filters, supportedEntities),
    userType: request.userType,
    sortBy: request.sortBy,
    interval: request.interval
  }

  return filterRequest;
}

export const formatTimespent = (timeSpent = 0) => {
  let timeSpentValue = `~${Math.floor(timeSpent)}s`;

  if (timeSpent >= 60) {
    timeSpentValue = `~${Math.round(timeSpent / 60)}m`;
  }
  
  return timeSpentValue;
};

export function getAuthorName(author: Author): string {
  return `${author.firstName ?? ""} ${author.lastName ?? ""}`.trim();
}

/**
 * activeTableEntityFilter - Returns only the valid applied entities for the given tab and table.
 * 
 * @param tabName - report tab name
 * @param tableName - report table name under the tab
 * @param appliedEntity - List of entities applied by the user
 * @returns Array of valid applied entities.
*/

export const activeTableEntityFilter = (tabName: string, tableName: string, appliedEntity: string[]) => {
    const allowedEntities: string[] = reportsFilterMapping[tabName][tableName];

    if(_.size(appliedEntity) > 0 && _.size(allowedEntities) > 0) {
      const filteredEntity =  _.filter(allowedEntities, (entity: string) => _.includes(appliedEntity, entity));
      const matchedEntityNames = _.map(
        _.filter(REPORTS_FILTERS, (item) => _.includes(filteredEntity, item.value)),
        "name"
      );
      return matchedEntityNames;
    }

    return [];
}

/**
 * Returns a readable label for the given applied date range.
 *
 * @param appliedDate - Object containing type and optional start/end dates or sometimes null
 * @returns Formatted date range string (e.g. "01/08/2025 - 05/08/2025") or predefined label (e.g. "7 Days"), or null if not matched
 */
export const getAppliedDateLabel = (appliedDate: AppliedDate | null ) => {
  let formatedAppliedDate: string | null = null;
  if(appliedDate){
    if(appliedDate.type === "custom") {
      const startDate = _.get(appliedDate, "startDate") ?? moment();
      const endDate = _.get(appliedDate, "endDate") ?? moment();
      formatedAppliedDate = `${dateFormat(startDate.toDate(), "dd/mm/yyyy")} - ${dateFormat(endDate.toDate(), "dd/mm/yyyy")}`;
    } else {
      formatedAppliedDate = _.find(DATE_RANGES, ['label', appliedDate.type])?.value ?? null;
    }
  }
  return formatedAppliedDate;
}

export const INTERNAL_OPERATORS: Operators[] = [
  Operators.is_internal,
  Operators.is_not_internal,
];

function isOperator(value: any): value is Operators {
  return typeof value === "string" && (Object.values(Operators) as string[]).includes(value);
}

export function hideInputField(operator: string |  null): boolean {
  return isOperator(operator) && INTERNAL_OPERATORS.includes(operator as Operators);
}

export const calculatePercentage = (value: number, total: number): string => {
  if (!total || total <= 0) return "0%";

  const percentage = (value / total) * 100;
  const normalizedPercentage = Math.max(0, Math.min(percentage, 100));

  return `${normalizedPercentage.toFixed(0)}%`;
};
